import "./AnnouncementReplies.scss";

import dayjsTZ from "dayjs";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Chart } from "react-google-charts";
import { onResend } from "../utils/methods";
import { ModalXIcon } from "../../../assets/icons";
import { CloseCircleFilled } from "@ant-design/icons";
import { Modal, Select, Skeleton, List, Avatar } from "antd";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import useEmployeeFilters from "./hooks/useEmployeeFilters";
import useAnnouncementData from "./hooks/useAnnouncementData";

//region CONSTANTS
export const REPLIES = {
  confirm: "Konfirmoi",
  skip: "Anashkaloi",
  refuse: "Refuzoi",
};

const BUTTON_CLASSES = {
  refuse: "mondayButtonRed",
  skip: "mondayButtonYellow",
  confirm: "mondayButtonGreen",
};

export const COLORS = {
  confirm: "#1da193",
  skip: "#e6951e",
  refuse: "#eb4345",
};

const CHART_OPTIONS = {
  title: "Grafiku i pergjigjeve te punonjesve",
  height: 400,
  bar: { groupWidth: "95%" },
  legend: { position: "none" },
};

// Helper functions
export const getEmployeeName = (employee) =>
  `${employee.employeeFirstName} ${employee.employeeLastName}`;

const getResponseColor = (response) => COLORS[response] || COLORS.refuse;

const AnnouncementReplies = ({ open, announcement, setOpen, filterBy }) => {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const { employees } = useSelector((state) => state.employeesList);

  const { activeFilter, updateFilter, clearFilters } = useEmployeeFilters();
  const { responses, departments, chartData } = useAnnouncementData(
    announcement,
    employees,
    filterBy
  );

  const filteredResponses = useMemo(() => {
    if (!Object.keys(activeFilter).length) return responses;

    return responses.filter((response) =>
      Object.entries(activeFilter).every(
        ([key, value]) => value && response[key] === value
      )
    );
  }, [responses, activeFilter]);

  const handleResend = async (employeeId, announcementId) => {
    try {
      await onResend(employeeId, announcementId);
    } catch (error) {
      console.error("Failed to resend:", error);
    }
  };

  //region RETURN
  return (
    <Modal
      open={open}
      title={announcement?.announcementTitle || "Përgjigjet e punonjësve"}
      destroyOnHidden={true}
      centered={true}
      closeIcon={<ModalXIcon />}
      onCancel={() => setOpen(false)}
      footer={null}
      className={`userResponsesModal ${
        isDarkMode ? "dark darkHeader" : "lightHeader"
      }`}
    >
      <div className="charty">
        {chartData ? (
          <Chart
            chartType="BarChart"
            height="350px"
            data={chartData}
            options={CHART_OPTIONS}
          />
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          gap: 20,
        }}
      >
        {!filterBy &&
          Object.entries(REPLIES).map(([key, label]) => (
            <MondayButton
              key={key}
              className={`${BUTTON_CLASSES[key]} ${
                activeFilter.response !== key ? "mondayButtonOutlined" : ""
              }`}
              onClick={() => updateFilter("response", key)}
            >
              {label}
            </MondayButton>
          ))}
        <Select
          placeholder="Zgjidh departamentin"
          classNames={{ popup: { root: isDarkMode ? "darkDropdown" : undefined } }}
          style={{ width: 250, borderRadius: 5 }}
          allowClear
          value={activeFilter.employeeDepartmentName}
          onChange={(selected) =>
            updateFilter("employeeDepartmentName", selected)
          }
          options={departments.map((dept) => ({ value: dept, label: dept }))}
        />
        <Select
          placeholder="Zgjidh punonjesin"
          classNames={{ popup: { root: isDarkMode ? "darkDropdown" : undefined } }}
          style={{ width: 250, borderRadius: 5 }}
          value={activeFilter.employeeId}
          onChange={(selected) => updateFilter("employeeId", selected)}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input?.toLowerCase())
          }
          options={employees?.map((employee) => ({
            value: employee.employeeId,
            label: getEmployeeName(employee),
          }))}
        />
        {Object.keys(activeFilter).length > 0 && (
          <MondayButton
            className="mondayButtonGrey"
            Icon={<CloseCircleFilled />}
            onClick={clearFilters}
          >
            Fshi filtrat
          </MondayButton>
        )}
      </div>
      {filteredResponses?.length > 0 ? (
        <List
          className="demo-loadmore-list"
          style={{
            overflowY: "auto",
            maxHeight: 350,
          }}
          // loading={initLoading}
          itemLayout="horizontal"
          // loadMore={loadMore}
          dataSource={filteredResponses}
          renderItem={(item) => (
            <List.Item
              actions={
                item?.response === "refuse"
                  ? [
                      <a
                        key="resend-action"
                        onClick={() =>
                          handleResend(item.employeeId, item.announcementId)
                        }
                      >
                        ridergo
                      </a>,
                    ]
                  : []
              }
            >
              <Skeleton avatar loading={false} active={false}>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: getResponseColor(item.response),
                      }}
                    >
                      {item.employeeName?.[0]?.toUpperCase()}
                    </Avatar>
                  }
                  title={
                    <a href="https://portal.flex.al/punonjesit">
                      {item.employeeName}
                    </a>
                  }
                  description={`${(
                    REPLIES[item.response] || ""
                  ).toLowerCase()} kete lajmerim per here te fundit ne - ${dayjsTZ(
                    item.updatedAt
                  ).format("DD/MM/YYYY HH:mm")}`}
                />
              </Skeleton>
            </List.Item>
          )}
        />
      ) : null}
    </Modal>
  );
};

export default AnnouncementReplies;
